import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  IconChevronDown,
  IconChevronRight,
  IconFile,
  IconFolder,
  IconFolderOpen,
  IconTrash,
} from '@tabler/icons-react';
import { Button } from '@/admin/components/ui/button';
import type { SkillFileInfo } from '@/admin/lib/api';
import { getFilesFromDataTransfer } from './file-utils';

export interface FileTreeNode {
  name: string;
  path: string;
  isDir: boolean;
  size?: number;
  children?: FileTreeNode[];
}

export const buildFileTree = (files: SkillFileInfo[]): FileTreeNode[] => {
  const root: FileTreeNode[] = [];
  const map: Record<string, FileTreeNode> = {};

  // Sort files: directories first, then alphabetically
  const sortedFiles = [...files].sort((a, b) => {
    return a.path.localeCompare(b.path);
  });

  sortedFiles.forEach(file => {
    // Clean path just in case
    const cleanPath = file.path.replace(/^\/+/, '');
    const parts = cleanPath.split('/');
    let currentPath = '';

    parts.forEach((part, index) => {
      const isLast = index === parts.length - 1;
      const parentPath = currentPath;
      currentPath = currentPath ? `${currentPath}/${part}` : part;

      if (!map[currentPath]) {
        const node: FileTreeNode = {
          name: part,
          path: currentPath,
          isDir: !isLast || file.is_directory,
          size: isLast && !file.is_directory ? file.size : undefined,
          children: !isLast || file.is_directory ? [] : undefined,
        };
        map[currentPath] = node;

        if (parentPath) {
          const parent = map[parentPath];
          if (parent && parent.children) {
            parent.children.push(node);
          }
        } else {
          root.push(node);
        }
      }
    });
  });

  // Sort children of each node
  const sortNodes = (nodes: FileTreeNode[]) => {
    nodes.sort((a, b) => {
      if (a.isDir === b.isDir) {
        return a.name.localeCompare(b.name);
      }
      return a.isDir ? -1 : 1;
    });
    nodes.forEach(node => {
      if (node.children) {
        sortNodes(node.children);
      }
    });
  };

  sortNodes(root);
  return root;
};

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
}

interface FileTreeItemProps {
  node: FileTreeNode;
  level: number;
  selectedFile: string | null;
  onSelect: (path: string) => void;
  onDelete: (path: string) => void;
  onDrop?: (files: File[], targetFolder: string) => Promise<void>;
}

const FileTreeItem: React.FC<FileTreeItemProps> = ({
  node,
  level,
  selectedFile,
  onSelect,
  onDelete,
  onDrop,
}) => {
  const [isOpen, setIsOpen] = useState(level === 0);
  const [isDragOver, setIsDragOver] = useState(false);
  const hasChildren = node.children && node.children.length > 0;
  const isSelected = selectedFile === node.path;
  const expandTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearExpandTimer = useCallback(() => {
    if (expandTimerRef.current) {
      clearTimeout(expandTimerRef.current);
      expandTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return clearExpandTimer;
  }, [clearExpandTimer]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (node.isDir) {
      setIsOpen(!isOpen);
    } else {
      onSelect(node.path);
    }
  };

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!node.isDir || !onDrop) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);

      // Auto-expand after 500ms
      if (!isOpen && !expandTimerRef.current) {
        expandTimerRef.current = setTimeout(() => {
          setIsOpen(true);
          expandTimerRef.current = null;
        }, 500);
      }
    },
    [node.isDir, onDrop, isOpen]
  );

  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (!node.isDir || !onDrop) return;
      e.preventDefault();
      e.stopPropagation();
      // Ignore leave events when moving to a child element
      if (e.currentTarget.contains(e.relatedTarget as Node)) return;
      setIsDragOver(false);
      clearExpandTimer();
    },
    [node.isDir, onDrop, clearExpandTimer]
  );

  const handleDrop = useCallback(
    async (e: React.DragEvent) => {
      if (!node.isDir || !onDrop) return;
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);
      clearExpandTimer();

      const droppedFiles = await getFilesFromDataTransfer(e.dataTransfer);
      if (droppedFiles.length > 0) {
        setIsOpen(true);
        await onDrop(droppedFiles, node.path);
      }
    },
    [node.isDir, node.path, onDrop, clearExpandTimer]
  );

  return (
    <div>
      <div
        className={`group flex items-center justify-between px-2 py-1.5 rounded-md transition-colors cursor-pointer text-sm select-none ${
          isDragOver
            ? 'ring-2 ring-primary/40 bg-primary/5'
            : isSelected
              ? 'bg-primary/10 text-primary'
              : 'hover:bg-muted/50 text-foreground/80'
        }`}
        style={{ paddingLeft: `${level * 12 + 8}px` }}
        onClick={handleClick}
        onDragOver={node.isDir ? handleDragOver : undefined}
        onDragLeave={node.isDir ? handleDragLeave : undefined}
        onDrop={node.isDir ? handleDrop : undefined}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {node.isDir ? (
            <div
              className={`p-0.5 rounded-sm hover:bg-muted-foreground/10 transition-colors ${!hasChildren ? 'invisible' : ''}`}
              onClick={hasChildren ? handleToggle : undefined}
            >
              {isOpen ? (
                <IconChevronDown className="h-3 w-3 text-muted-foreground" />
              ) : (
                <IconChevronRight className="h-3 w-3 text-muted-foreground" />
              )}
            </div>
          ) : (
            <div className="w-4" />
          )}

          {node.isDir ? (
            isOpen ? (
              <IconFolderOpen className="h-4 w-4 text-blue-500/80 shrink-0" />
            ) : (
              <IconFolder className="h-4 w-4 text-blue-500/80 shrink-0" />
            )
          ) : (
            <IconFile className="h-4 w-4 text-muted-foreground shrink-0" />
          )}

          <span className="truncate font-medium">{node.name}</span>
          {!node.isDir && node.size !== undefined && (
            <span className="ml-2 text-[10px] text-muted-foreground/60 shrink-0">
              {formatFileSize(node.size)}
            </span>
          )}
        </div>

        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 -mr-1"
          onClick={e => {
            e.stopPropagation();
            onDelete(node.path);
          }}
        >
          <IconTrash className="h-3.5 w-3.5 text-destructive/70 hover:text-destructive" />
        </Button>
      </div>

      {node.isDir && isOpen && node.children && (
        <div className="flex flex-col">
          {node.children.map(child => (
            <FileTreeItem
              key={child.path}
              node={child}
              level={level + 1}
              selectedFile={selectedFile}
              onSelect={onSelect}
              onDelete={onDelete}
              onDrop={onDrop}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export const FileTreeView: React.FC<{
  files: SkillFileInfo[];
  selectedFile: string | null;
  onSelect: (path: string) => void;
  onDelete: (path: string) => void;
  onDrop?: (files: File[], targetFolder: string) => Promise<void>;
}> = ({ files, selectedFile, onSelect, onDelete, onDrop }) => {
  const [isDragOverContainer, setIsDragOverContainer] = useState(false);
  const root = buildFileTree(files);

  const handleContainerDragOver = useCallback(
    (e: React.DragEvent) => {
      if (!onDrop) return;
      e.preventDefault();
      setIsDragOverContainer(true);
    },
    [onDrop]
  );

  const handleContainerDragLeave = useCallback(
    (e: React.DragEvent) => {
      if (!onDrop) return;
      e.preventDefault();
      // Only reset when leaving the container itself, not children
      if (e.currentTarget === e.target) {
        setIsDragOverContainer(false);
      }
    },
    [onDrop]
  );

  const handleContainerDrop = useCallback(
    async (e: React.DragEvent) => {
      if (!onDrop) return;
      e.preventDefault();
      setIsDragOverContainer(false);

      const droppedFiles = await getFilesFromDataTransfer(e.dataTransfer);
      if (droppedFiles.length > 0) {
        await onDrop(droppedFiles, '');
      }
    },
    [onDrop]
  );

  if (files.length === 0) {
    return (
      <div
        className={`py-8 text-center border rounded-lg transition-colors ${
          isDragOverContainer
            ? 'border-primary/40 bg-primary/5'
            : 'bg-muted/20'
        }`}
        onDragOver={handleContainerDragOver}
        onDragLeave={handleContainerDragLeave}
        onDrop={handleContainerDrop}
      >
        <IconFile className="h-6 w-6 mx-auto mb-2 text-muted-foreground/40" />
        <p className="text-xs text-muted-foreground">
          {isDragOverContainer ? 'Drop files here' : 'No files uploaded'}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`border rounded-lg overflow-hidden bg-card p-1 transition-colors ${
        isDragOverContainer ? 'ring-1 ring-primary/20 bg-primary/[0.02]' : ''
      }`}
      onDragOver={handleContainerDragOver}
      onDragLeave={handleContainerDragLeave}
      onDrop={handleContainerDrop}
    >
      {root.map(node => (
        <FileTreeItem
          key={node.path}
          node={node}
          level={0}
          selectedFile={selectedFile}
          onSelect={onSelect}
          onDelete={onDelete}
          onDrop={onDrop}
        />
      ))}
    </div>
  );
};
